import { compareAnalyticsFormattedDates } from "./analyticsDateRange";

/**
 * Carousel / component analytics identity.
 *
 * Add/Edit allows duplicate display titles; rows are identified by DB `id`
 * (and opaque `unique_id`). Analytics must key on those — never title alone.
 */

export function parseAnalyticsDetails(
  raw: unknown,
): Record<string, any> {
  if (raw == null) return {};
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return raw as Record<string, any>;
  }
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, any>;
      }
    } catch {
      return {};
    }
  }
  return {};
}

function trimStr(v: unknown): string {
  return v != null ? String(v).trim() : "";
}

/** True when the row has a stable identity (not just a display title). */
export function carouselHasStrongIdentity(elem: any): boolean {
  return Boolean(trimStr(elem?.id) || trimStr(elem?.unique_id));
}

/** Outer map key for carousel click/view buckets. Prefer id over title. */
export function carouselAnalyticsBucketKey(
  details: Record<string, any> | null | undefined,
): string {
  const d = details || {};
  const componentId = trimStr(d.component_id);
  if (componentId) return componentId;
  const uniqueId = trimStr(d.unique_id);
  if (uniqueId) return uniqueId;
  // Legacy individual-page Post Click used `id` (often unique_id / route_name).
  const legacyId = trimStr(d.id);
  if (legacyId) return legacyId;
  const title = trimStr(d.title);
  return title || "Untitled";
}

/**
 * Strong identity keys to alias a bucket under (same array reference).
 * Excludes display title so two carousels named alike never share a key —
 * except links-section (see below).
 */
export function carouselAnalyticsAliasKeys(
  details: Record<string, any> | null | undefined,
): string[] {
  const d = details || {};
  const keys: string[] = [];
  const push = (v: unknown) => {
    const s = trimStr(v);
    if (s && !keys.includes(s)) keys.push(s);
  };
  push(d.component_id);
  push(d.unique_id);
  push(d.id);
  const primary = carouselAnalyticsBucketKey(d);
  if (primary && !keys.includes(primary)) keys.unshift(primary);
  // Legacy LiB link clicks only stored section `title` ("My Links"). Modern
  // events also set component_id. Alias title so both land in one bucket —
  // otherwise Analytics shows ~component_id-only clicks (e.g. 58) while
  // thousands of title-keyed clicks are orphaned.
  if (isLinksSectionAnalyticsPlatform(d.platform)) {
    push(d.title);
  }
  return keys;
}

/**
 * Store `bucket` under every strong alias so lookup by id / unique_id / route
 * slug all resolve to the same series — without keying by display title.
 */
export function assignCarouselAnalyticsBucket<T>(
  acc: Record<string, T>,
  details: Record<string, any> | null | undefined,
  bucket: T,
): T {
  const aliases = carouselAnalyticsAliasKeys(details);
  if (!aliases.length) {
    const fallback = carouselAnalyticsBucketKey(details);
    acc[fallback] = bucket;
    return bucket;
  }
  for (const key of aliases) {
    acc[key] = bucket;
  }
  return bucket;
}

function linksSectionComponentId(
  details: Record<string, any> | null | undefined,
): string {
  const d = details || {};
  return trimStr(d.component_id) || trimStr(d.id);
}

/** Strong ids already pointing at `bucket` (excludes the section display title). */
function bucketOwnedByOtherLinksSection(
  acc: Record<string, any>,
  bucket: any,
  details: Record<string, any> | null | undefined,
): boolean {
  if (!isLinksSectionAnalyticsPlatform(details?.platform)) return false;
  const myId = linksSectionComponentId(details);
  const titleKey = trimStr(details?.title);
  if (!myId || !titleKey) return false;
  for (const [key, value] of Object.entries(acc)) {
    if (value !== bucket) continue;
    if (key === titleKey) continue;
    if (key === myId) continue;
    if (key === trimStr(details?.unique_id)) continue;
    return true;
  }
  return false;
}

/** Get or create the shared array bucket for a click/view details payload. */
export function ensureCarouselAnalyticsBucket(
  acc: Record<string, any[]>,
  details: Record<string, any> | null | undefined,
): any[] {
  for (const key of carouselAnalyticsAliasKeys(details)) {
    if (!Array.isArray(acc[key])) continue;
    if (bucketOwnedByOtherLinksSection(acc, acc[key], details)) continue;
    assignCarouselAnalyticsBucket(acc, details, acc[key]);
    return acc[key];
  }
  const bucket: any[] = [];
  assignCarouselAnalyticsBucket(acc, details, bucket);
  return bucket;
}

/** Zero-fill a per-day clicks series and alias it under every strong identity. */
export function ensureCarouselDateSeriesBucket(
  acc: Record<string, Array<{ formatted_date: string; clicks: number }>>,
  details: Record<string, any> | null | undefined,
  allDates: Iterable<string>,
): Array<{ formatted_date: string; clicks: number }> {
  for (const key of carouselAnalyticsAliasKeys(details)) {
    if (!Array.isArray(acc[key])) continue;
    if (bucketOwnedByOtherLinksSection(acc, acc[key], details)) continue;
    assignCarouselAnalyticsBucket(acc, details, acc[key]);
    return acc[key];
  }
  const series = Array.from(allDates)
    .slice()
    .sort(compareAnalyticsFormattedDates)
    .map((date) => ({
      formatted_date: date,
      clicks: 0,
    }));
  assignCarouselAnalyticsBucket(acc, details, series);
  return series;
}

/** Fields to include on Carousel Click / Post Click `details`. */
export function carouselTrackIdentity(component: any): {
  component_id?: string;
  unique_id?: string;
  title?: string;
  type?: string;
} {
  const component_id =
    component?.id != null && String(component.id).trim() !== ""
      ? String(component.id)
      : undefined;
  const unique_id =
    component?.unique_id != null && String(component.unique_id).trim() !== ""
      ? String(component.unique_id)
      : undefined;
  const title =
    component?.name != null ? String(component.name) : undefined;
  const type =
    component?.type != null ? String(component.type) : undefined;
  return {
    ...(component_id ? { component_id } : {}),
    ...(unique_id ? { unique_id } : {}),
    ...(title != null ? { title } : {}),
    ...(type ? { type } : {}),
  };
}

/** Ordered lookup keys for a component row against an analytics map. */
export function carouselAnalyticsLookupKeys(elem: any): string[] {
  const keys: string[] = [];
  const push = (v: unknown) => {
    const s = trimStr(v);
    if (s && !keys.includes(s)) keys.push(s);
  };
  push(elem?.id);
  push(elem?.unique_id);
  push(elem?.route_name);
  // Name is last-resort only — see pickCarouselAnalyticsBucket.
  push(elem?.name);
  return keys;
}

function titleIsDuplicateAmong(
  allElems: any[] | null | undefined,
  name: string,
): boolean {
  if (!name || !Array.isArray(allElems)) return false;
  return allElems.filter((e) => trimStr(e?.name) === name).length > 1;
}

/** Merge click/view row arrays by post_id (sum count / idCount). */
export function mergeCarouselAnalyticsRowBuckets(
  buckets: any[][],
): any[] {
  const byPostId = new Map<string, any>();
  const order: string[] = [];
  for (const bucket of buckets) {
    if (!Array.isArray(bucket)) continue;
    for (const row of bucket) {
      const postId = trimStr(row?.post_id) || `row-${order.length}`;
      const existing = byPostId.get(postId);
      if (!existing) {
        byPostId.set(postId, { ...row });
        order.push(postId);
        continue;
      }
      existing.count = (existing.count || 0) + (row?.count || 0);
      existing.idCount = (existing.idCount || 0) + (row?.idCount || 0);
      if (row?.id && !existing.id) existing.id = row.id;
      if (
        row?.created_at &&
        (!existing.created_at ||
          new Date(row.created_at) > new Date(existing.created_at))
      ) {
        existing.name = row.name ?? existing.name;
        existing.created_at = row.created_at;
      }
    }
  }
  return order.map((id) => byPostId.get(id)).filter(Boolean);
}

/** Merge per-day click series by formatted_date (sum clicks). */
export function mergeCarouselAnalyticsDateSeriesBuckets(
  buckets: any[][],
): Array<{ formatted_date: string; clicks: number }> {
  const byDate = new Map<string, number>();
  for (const bucket of buckets) {
    if (!Array.isArray(bucket)) continue;
    for (const row of bucket) {
      const date = trimStr(row?.formatted_date);
      if (!date) continue;
      byDate.set(date, (byDate.get(date) || 0) + Number(row?.clicks || 0));
    }
  }
  return Array.from(byDate.entries())
    .sort((a, b) => compareAnalyticsFormattedDates(a[0], b[0]))
    .map(([formatted_date, clicks]) => ({ formatted_date, clicks }));
}

/** Merge carousel view totals (unique_id rows → one KPI-friendly total). */
export function mergeCarouselAnalyticsViewBuckets(buckets: any[][]): any[] {
  let clicks = 0;
  for (const bucket of buckets) {
    if (!Array.isArray(bucket)) continue;
    for (const row of bucket) {
      clicks += Number(row?.clicks || 0);
    }
  }
  return clicks > 0 ? [{ clicks }] : [];
}

function carouselAnalyticsBucketKind(
  bucket: any[],
): "date" | "views" | "rows" {
  if (!Array.isArray(bucket) || !bucket.length) return "rows";
  if (bucket.some((row) => row && "formatted_date" in row)) return "date";
  // Views: { unique_id?, clicks } without post click fields.
  if (
    bucket.some(
      (row) =>
        row &&
        "clicks" in row &&
        !("post_id" in row) &&
        !("count" in row) &&
        !("idCount" in row),
    )
  ) {
    return "views";
  }
  return "rows";
}

/** Merge matched analytics buckets (rows, date series, or view totals). */
export function mergeCarouselAnalyticsBuckets(buckets: any[][]): any[] {
  const kinds = new Set(
    buckets.filter(Array.isArray).map((b) => carouselAnalyticsBucketKind(b)),
  );
  if (kinds.has("date")) return mergeCarouselAnalyticsDateSeriesBuckets(buckets);
  if (kinds.has("views") && !kinds.has("rows")) {
    return mergeCarouselAnalyticsViewBuckets(buckets);
  }
  return mergeCarouselAnalyticsRowBuckets(buckets);
}

/**
 * Resolve analytics for a component.
 *
 * Prefer id / unique_id, and when the display title is unique among siblings
 * also fold in legacy title-keyed buckets (Featured Content, etc.). Returning
 * only the first id hit dropped historical title-keyed clicks after identity
 * tracking shipped.
 *
 * Duplicate titles never fall back to name — those tabs would otherwise share
 * the same merged counts.
 */
export function pickCarouselAnalyticsBucket<T>(
  map: Record<string, T> | null | undefined,
  elem: any,
  allElems?: any[] | null,
): T | undefined {
  if (!map) return undefined;
  const strong = carouselHasStrongIdentity(elem);
  const name = trimStr(elem?.name);

  const matched: any[][] = [];
  const seen = new Set<any[]>();
  for (const key of carouselAnalyticsLookupKeys(elem)) {
    if (strong && name && key === name) {
      // Unique-title merge for legacy rows keyed only by title.
      if (titleIsDuplicateAmong(allElems, name)) continue;
    }
    if (!Object.prototype.hasOwnProperty.call(map, key)) continue;
    const bucket = map[key];
    if (!Array.isArray(bucket)) return bucket as T;
    if (seen.has(bucket)) continue;
    seen.add(bucket);
    matched.push(bucket);
  }
  if (!matched.length) return undefined;
  if (matched.length === 1) return matched[0] as T;
  return mergeCarouselAnalyticsBuckets(matched) as T;
}

/** Rank from a map keyed by id/unique_id/title (lower = more popular). */
export function carouselAnalyticsRank(
  rankMap: Map<string, number> | null | undefined,
  elem: any,
): number {
  if (!rankMap || rankMap.size === 0) return Number.POSITIVE_INFINITY;
  for (const key of carouselAnalyticsLookupKeys(elem)) {
    if (rankMap.has(key)) return rankMap.get(key) as number;
  }
  return Number.POSITIVE_INFINITY;
}

/**
 * Links-section Post Clicks always carry `id` (component id). That must not be
 * treated as a separate "page click" bucket — carousels use idCount for that.
 */
export function isLinksSectionAnalyticsPlatform(platform: unknown): boolean {
  const p = String(platform || "").trim().toLowerCase();
  return (
    p === "links-section" || p === "link-section" || p === "link_section"
  );
}

/**
 * Component widgets (banner, highlight, podcast, …) and identity-era Post Clicks
 * put the stayuptodate component id in `details.id` / `component_id`. That is
 * identity for bucketing — not the legacy individual-page "Page" click channel
 * (`idCount`). Fold those into `count` only; otherwise the first event is
 * stored as count=N and idCount=N → analytics shows 2× on the first click.
 */
export function foldCarouselClickIntoCountOnly(
  post: Record<string, any> | null | undefined,
): boolean {
  const d = post || {};
  if (isLinksSectionAnalyticsPlatform(d.platform)) return true;

  const componentId = trimStr(d.component_id);
  const postId = trimStr(d.post_id);
  const id = trimStr(d.id);
  // Widget clicks use the component id as post_id (not a feed post id).
  if (componentId && postId && postId === componentId) return true;
  if (componentId && id && id === componentId && postId === id) return true;

  const p = String(d.platform || "").trim().toLowerCase();
  return (
    p === "banner" ||
    p === "highlight" ||
    p === "highlights" ||
    p === "campaign" ||
    p === "podcast" ||
    p === "latest-podcast"
  );
}
